/**
 * THE FOUR FACES THAT ASK HOW A CLEAN WAS, each a link to Feedback on the clean with that face chosen
 * (`feedbackPath`), where nothing is sent until she presses Send: drawn alike on Home's last clean
 * and on one visit's report (`screens/VisitReport.tsx`, home #21). Each face is its icon over its
 * name, in the order the artboards draw them (`FACES`).
 *
 * - THE FACE SHE GAVE, where Home lights it (`lit`, her own newest answer from home standing, P41),
 *   is drawn as a sent answer is, in the navy fill, and marked current; every face still leads to
 *   Feedback, since she may answer again.
 * - AMONG SEVERAL HOMES each face is described by the home's name (`describedBy`), so the faces of
 *   two cards are never heard alike.
 */
import { Link } from 'react-router-dom';
import { feedbackPath } from '../paths.ts';
import { FACES, type Face } from '../words.ts';
import { FaceIcon } from './FaceIcon.tsx';

interface FaceLinksProps {
  /** Ops's id of the clean the faces ask about. */
  readonly visitId: string;
  /** The face she gave, drawn as sent, or none. */
  readonly lit?: Face | undefined;
  /** The id of the home's name, which describes each face among several homes, or none. */
  readonly describedBy?: string | undefined;
  /** The space above the faces, as the card they are drawn on sets it. */
  readonly className?: string | undefined;
}

export function FaceLinks({ visitId, lit, describedBy, className }: FaceLinksProps) {
  return (
    <div className={`${className === undefined ? '' : `${className} `}grid grid-cols-4`}>
      {FACES.map(({ face, label }) => (
        <Link
          key={face}
          to={feedbackPath(visitId, face)}
          aria-describedby={describedBy}
          aria-current={face === lit ? 'true' : undefined}
          className="group flex flex-col items-center gap-1 rounded-control py-1"
        >
          <span
            className={`flex size-15 items-center justify-center rounded-full border-2 ${
              face === lit
                ? 'border-surface-brand bg-surface-brand text-text-on-brand'
                : 'border-border-strong bg-surface-raised text-text-brand group-hover:bg-surface-accent-soft'
            }`}
          >
            <FaceIcon face={face} />
          </span>
          <span
            className={`text-center text-small whitespace-nowrap text-text-body ${face === lit ? 'font-semibold' : ''}`}
          >
            {label}
          </span>
        </Link>
      ))}
    </div>
  );
}
